import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '@shared/components/Button';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2, MBImage } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { getComponentIcon, getComponentPlaceholder } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { SignatureClearButton } from './SignatureClearButton';
import styles from './SignatureView.module.css';

export interface SignatureProps {
  component: API.SignatureComponent;
  signatureUrl?: string;
  onSignatureUpdate(data: string): void;
  isLoading?: boolean;
}

export function SignatureView({
  component,
  onSignatureUpdate,
  signatureUrl,
  isLoading,
}: SignatureProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<SignatureCanvas | null>(null);
  const [currentDataUrl, setCurrentDataUrl] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [canvasKey, setCanvasKey] = useState(0);

  function onBegin() {
    setIsDrawing(true);
  }

  function onEnd() {
    setIsDrawing(false);
    setCurrentDataUrl(canvasRef.current?.toDataURL() ?? '');
  }

  const clear = () => {
    canvasRef.current?.clear();
    setCurrentDataUrl('');
    onSignatureUpdate('');
  };

  const onSave = () => {
    onSignatureUpdate(canvasRef.current?.toDataURL() ?? '');
    setCurrentDataUrl('');
    canvasRef.current?.clear();
  };

  const isReadOnly = component?.readonly ?? false;
  const isEmptySignature = isEmpty(currentDataUrl) && isEmpty(signatureUrl);
  const hasSignature = !isEmptySignature;
  const showCornerButton = hasSignature && !component?.readonly && !isReadOnly && !isDrawing;
  const showCanvas = !isReadOnly && isEmpty(signatureUrl);
  const showImage = !isEmpty(signatureUrl);
  const showSaveButtons = !isReadOnly && !isEmpty(currentDataUrl);

  useEffect(() => {
    // The signature canvas sizes its internal drawing buffer to its container on
    // mount. When the container is resized (e.g. resizing the card modal or
    // hiding the chat pane) the buffer no longer matches the rendered size, so
    // drawn strokes become offset from the cursor. Remount the canvas on resize
    // so it re-reads its dimensions and stays aligned.
    const element = containerRef.current;
    if (!element || isReadOnly) {
      return;
    }
    let lastWidth = element.clientWidth;
    let lastHeight = element.clientHeight;
    const observer = new ResizeObserver(() => {
      const { clientWidth, clientHeight } = element;
      if (clientWidth === lastWidth && clientHeight === lastHeight) {
        return;
      }
      lastWidth = clientWidth;
      lastHeight = clientHeight;
      setCanvasKey(key => key + 1);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [isReadOnly]);

  return (
    <View
      className={[
        styles.signatureView,
        isReadOnly && styles.readonly,
        isLoading && styles.isLoading,
        isDrawing && styles.isDrawing,
        hasSignature && styles.hasSignature,
      ]}
    >
      {showCornerButton && !showSaveButtons && <SignatureClearButton onPress={clear} />}
      <View ref={containerRef} className={styles.canvasArea}>
        {isLoading && (
          <View center className={styles.loading}>
            <MBLoadingIndicator />
          </View>
        )}
        <View center className={styles.placeholder}>
          <View row gap={1} className={styles.placeholderLabel}>
            <MBIconV2
              size={grid(5)}
              name={getComponentIcon(component) ?? 'signature-ballpoint-pen'}
            />
            <Text flex size="subtitle" className={styles.placeholderText}>
              {getComponentPlaceholder(component)}
            </Text>
          </View>
          <View className={styles.signatureLine} />
        </View>
        {showCanvas && (
          <SignatureCanvas
            key={canvasKey}
            ref={canvasRef}
            onBegin={onBegin}
            onEnd={onEnd}
            canvasProps={{ className: styles.canvas }}
          />
        )}
        {showImage && (
          <View className={styles.signatureImage}>
            <MBImage source={{ uri: signatureUrl }} fill resizeMode="contain" />
          </View>
        )}
      </View>
      {showSaveButtons && (
        <View row center gap={1} className={styles.saveButtons}>
          <Button variant="tertiary" size="medium" onClick={clear} label={i18n('common.clear')} />
          <Button variant="primary" size="medium" onClick={onSave} label={i18n('common.save')} />
        </View>
      )}
    </View>
  );
}
